import { useState, useMemo, useEffect, useRef } from 'react';
import { TOPGAMENUM } from '@/pages/intelligence/pages/TopCharts/_config/topChartsConst';
import { TableSelectionType } from '@/pages/intelligence/components/TableComponents/components/TableCheckbox';
import {
  handleTableDefaultSelectGames,
  sortTableByMetric,
} from '@/pages/intelligence/utils/tableUtils';
import type {
  Game,
  UseTopChartsConfig,
  UseTopChartsResponse,
  PageSortType,
} from '@intelligence/pages/TopCharts/PCGames/_config/type';
import type { IStoreRankDetailsRankTrendParams } from '@/pages/intelligence/types/storeRank';
import useCountriesNames from '@intelligence/hooks/useCountriesNames';
import {
  getMarketsValue,
  getMarketKeyList,
} from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/StoreRank/utils';
interface SortByItemType {
  ratioType: 0 | 1;
  sortVal: 'desc' | 'asc';
  sortMetric: string;
  sortSource?: string;
  key?: string;
}

interface ChangeMarketProfileAllParamsType {
  type: 'cancel' | 'all';
}
interface CountryType {
  code?: string;
  nameEn?: string;
  nameCh?: string;
}
interface ItemType extends Game {
  market?: string;
}

export default function useRankTrendModel<E extends Game>(
  config: UseTopChartsConfig,
): UseTopChartsResponse<E> {
  const {
    defaultParams = {},
    getTableApiFun,
    getTrendApiFun,
    basicInfo,
    regionOptions,
    rankType,
    isFreeCur: isFree,
    updateEarliestDateData,
  } = config || {};
  const { findCountryObj } = useCountriesNames();
  const [fetchParams, setFetchParams] = useState<IStoreRankDetailsRankTrendParams>(defaultParams);
  // 宽表总数据条数
  const [tableTotal, setTableTotal] = useState(0);
  // 宽表数据
  const [tableData, setTableData] = useState<E[]>([]);
  // 宽表缓存数据
  const [cacheTableData, setCacheTableData] = useState<E[]>([]);
  // echarts图表类d
  // 折线图数据源
  const [lineData, setLineData] = useState<any[]>([]);
  // 宽表勾选的数据
  const [defaultSelectionGames, setDefaultSelectionGames] = useState<TableSelectionType[]>([]);
  const [tableSelection, setTableSelection] = useState<TableSelectionType[]>([]);
  const sortMetric = isFree ? 'appstore top free' : 'appstore top paid';
  const [pageSortAttr, setPageSortAttr] = useState<PageSortType>({
    currentSortValue: 'desc',
    currentSortMetric: sortMetric,
    currentRatioType: 0,
    sortKey: `${sortMetric}_0`,
  });
  // 是否重新操作过复选器提交
  const [isNewComfirm, setIsNewComfirm] = useState(true);
  // 是否重新勾选过游戏
  const [isNewCheckGame, setIsNewCheckGame] = useState(true);
  /** 真正的是否重新勾选游戏， 上面那个isNewCheckGame需要做优化处理 */
  const [operatedGame, setOperatedGame] = useState(false);
  const [showNoGameExtendModal, setShowNoGameExtendModal] = useState(false);
  const [trendLoading, setTrendLoading] = useState(true);
  const [trendError, setTrendError] = useState(false);
  const [tableLoading, setTableLoading] = useState(true);
  // 是否进行了宽表的check操作
  const [isOperateCheckox, setIsOperateCheckbox] = useState<boolean>(false);
  const [tableSearchValue, setTableSearchValue] = useState('');
  const [reqDateObject, setReqDateObject] = useState({});
  const curRef = useRef({ last: false, radio: false });
  const isOperateRef = useRef(false);
  // 处理点击排序后勾选数据和请求折线的交互
  const updateGetGameData = (list: Game[]) => {
    let newList = [...list];
    updateSelection(newList);
    newList = newList.map((item, index) => ({ ...item, rank: index + 1 }));
    return newList.slice(0);
  };

  const updateSelection = function (sortTable: any[], isNeedLine = false) {
    const filterSelector = sortTable.slice(0, TOPGAMENUM);
    const defaultGames = handleTableDefaultSelectGames(filterSelector, 'market');
    setTimeout(() => {
      setDefaultSelectionGames(defaultGames);
      if (!isOperateRef.current) {
        setTableSelection(defaultGames);
        if (isNeedLine) {
          getLineChartData(getMarketKeyList(defaultGames));
        }
      }
    }, 1);
  };

  useEffect(() => {
    isOperateRef.current = isOperateCheckox;
  }, [isOperateCheckox]);
  const setBoolean = (bLast: boolean, radioDate: boolean) => {
    curRef.current.last = bLast;
    curRef.current.radio = radioDate;
  };
  const getTableData = async (
    argums: IStoreRankDetailsRankTrendParams,
    ignoreFetchParams = false,
    rankTypeValue = rankType,
  ) => {
    setTableLoading(true);
    setTrendLoading(true);
    const pageParamsTemp = {
      ...argums,
      unified_id: basicInfo?.id,
      // 排序指标，默认选择数据源第一个指标
      order_source: pageSortAttr?.currentSortMetric,
      order: pageSortAttr?.currentSortValue,
      ratio_type: pageSortAttr?.currentRatioType,
      regions: getMarketsValue(argums.regions, regionOptions),
    };

    // 无论请不请求接口，都先更新一下参数
    if (!ignoreFetchParams) {
      setFetchParams(pageParamsTemp);
      setIsOperateCheckbox(false);
    }
    // 仅当宽表类型为latest_ranking的时候，且仅调用宽表接口的时候，开始时间才为与结束时间同一天
    const pageParams = {
      ...pageParamsTemp,
      b_ratio_start_date: curRef.current.radio,
      b_last_date: curRef.current.last,
    };
    // if (rankTypeValue === 'latest_ranking') {
    //   pageParams = {
    //     ...pageParams,
    //     start_date: pageParams.end_date,
    //   };
    // }
    const isSearch = pageParams?.unified_ids?.length > 0;

    // eslint-disable-next-line no-prototype-builtins
    if (pageParams.hasOwnProperty('app_ids') && !pageParams.app_ids?.length) {
      setTableData([]);
      setTableTotal(0);
      if (!isSearch) {
        setLineData([]);
        setTableSelection([]);
      }
      setTableLoading(false);
      if (!isOperateRef.current) {
        setTrendLoading(false);
      }
      return;
    }

    try {
      setTableData([]);
      const resData = await getTableApiFun(pageParams);
      const list = resData?.data?.list || [];
      const trendList = resData?.data?.trends || [];
      const req = resData?.req;
      const { end_date, start_date, link_start_date, link_end_date } = resData?.data || {};
      // 请求接口后需要前端排序
      const newList = updateGetGameData(list);
      setCacheTableData(list);
      setTableData(newList);
      setTableTotal(newList?.length);
      setTableSearchValue('resetSearch');
      setReqDateObject({
        ...{ end_date, start_date, link_start_date, link_end_date },
      });
      // 仅改变筛选器和默认第一次的时候，需要重新设置起始时间参数
      if (req && !ignoreFetchParams) {
        // 如果数据没有正常返回则不设置
        const newFetchParams = {
          ...pageParams,
          start_date: req?.start_date || '',
          end_date: req?.end_date || '',
        };
        // 若此时是latest_ranking，则不需要更新时间参数
        if (rankTypeValue !== 'latest_ranking') {
          setFetchParams(newFetchParams);
        }
        const filterSelector = list.slice(0, 6);
        setTableSelection(filterSelector);
      }

      // 如果用户的勾选操作,或者切换宽表segment时,或者在latest改变筛选器的时候，需要调用可视化接口
      if (isOperateRef.current || ignoreFetchParams || rankTypeValue === 'latest_ranking') {
        getLineChartData(pageParams.regions, pageParamsTemp);
      } else {
        setLineData(trendList);
      }

      // 更新最早有数据时间
      updateEarliestDateData({
        entity_type: pageParams.entity_type,
        data_sources: pageParams.source,
        app_ids: pageParams.app_ids,
        metric: ['rank'],
        unified_id: pageParams?.unified_id,
        region_type: pageParams.region_type,
        regions: pageParams.regions,
        dateType: pageParams.granularity,
      });
    } catch (e) {
      console.error(e);
      // 如果是搜索的情况接口报错，不需要清空可视化
      setTableData([]);
      setTableTotal(0);
      if (!isSearch) {
        setLineData([]);
        setTableSelection([]);
      }
    } finally {
      setTableLoading(false);
      if (!isOperateRef.current) {
        setTrendLoading(false);
      }
    }
  };

  // 可视化请求接口函数
  const getLineChartData = async (
    keyList: string[],
    fetchParamsValue: any = fetchParams,
    isFromTable = false,
  ) => {
    // 如果数据没有正常返回则不设置
    const newFetchParams = {
      ...fetchParamsValue,
      regions: keyList || [],
    };

    if (newFetchParams?.regions?.length > 0) {
      try {
        setTrendLoading(true);
        const res = await getTrendApiFun(newFetchParams);
        const data = res?.data || [];
        setLineData(data || []);
        setIsNewComfirm(false);
        setIsNewCheckGame(false);
      } catch (e) {
        setLineData([]);
        setTrendError(true);
        console.error(e);
      } finally {
        setTrendLoading(false);
      }
    } else {
      // 未勾选任何游戏，不请求，返回为空数据
      setLineData([]);
    }
  };

  // 宽表数据是否有数据排查
  const isTableNodata = useMemo(
    () =>
      !(
        tableData?.findIndex(
          (item: any) => Array.isArray(item?.metrics) && item?.metrics?.length > 0,
        ) > -1
      ),
    [tableData],
  );

  // 可视化数据是否有数据排查
  const isLineNodata = useMemo(
    () =>
      !(
        tableSelection?.findIndex(
          (item: any) => Array.isArray(item?.metrics) && item?.metrics?.length > 0,
        ) > -1
      ),
    [tableSelection],
  );

  const changeMarketProfileAll = (params: ChangeMarketProfileAllParamsType) => {
    if (params.type === 'cancel') {
      setTableSelection([]);
    } else {
      setTableSelection(defaultSelectionGames);
      getLineChartData(getMarketKeyList(defaultSelectionGames));
    }
  };

  const updateTableField = function (param: string) {
    setTableSearchValue(param);
    if (!param?.length) {
      setTableData([...cacheTableData]);
      return;
    }
    const countryObj: CountryType = findCountryObj(param) || {};
    const { code = '' } = countryObj;
    const newTableData = [...tableData].filter((item: ItemType) => item.market == code);
    setTableData(newTableData);
  };

  const onSortChage = (e: SortByItemType) => {
    setPageSortAttr({
      currentSortValue: e.sortVal,
      currentSortMetric: e.sortMetric,
      currentRatioType: e.ratioType,
      sortKey: e.key,
    });
    const isRank = e.ratioType == 0;
    let sortTable = sortTableByMetric({
      tableData,
      sortParams: e,
      isRank,
      growthProperty: 'dod',
    });
    updateSelection(sortTable, true);
    sortTable = sortTable.map((item, index) => ({ ...item, rank: index + 1 }));
    setTableData(sortTable.slice(0));
  };

  const resetPageSortAttr = function () {
    setPageSortAttr({
      currentSortValue: 'desc',
      currentSortMetric: sortMetric,
      currentRatioType: 0,
      sortKey: `${sortMetric}_0`,
    });
  };

  return {
    fetchParams,
    tableTotal,
    tableData,
    lineData,
    trendLoading,
    tableLoading,
    trendError,
    defaultSelectionGames,
    isNewComfirm,
    setIsNewComfirm,
    isNewCheckGame,
    setIsNewCheckGame,
    tableSelection,
    operatedGame,
    setOperatedGame,
    setTableSelection,
    isTableNodata,
    isLineNodata,
    showNoGameExtendModal,
    setShowNoGameExtendModal,
    getTableData,
    setFetchParams,
    changeMarketProfileAll,
    updateTableField,
    onSortChage,
    pageSortAttr,
    tableSearchValue,
    resetPageSortAttr,
    setIsOperateCheckbox,
    getLineChartData,
    reqDateObject,
    setBoolean,
  };
}
